import PropTypes from 'prop-types';
import last from 'lodash/last';
import initial from 'lodash/initial';
import tail from 'lodash/tail';
import first from 'lodash/first';
import mapValues from 'lodash/mapValues';
import keyBy from 'lodash/keyBy';
import compact from 'lodash/compact';
import map from 'lodash/map';
import filter from 'lodash/filter';
import find from 'lodash/find';
import flatMap from 'lodash/flatMap';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Timeline from '../../../../common/components/Timeline';
import ProjectSelect from '../../../../common/selectors/Project';
import MilestonesModal from './MilestonesModal';
import PatientRecord from '../../../../common/models/PatientRecord';
import ProjectMilestone from '../../../../common/models/ProjectMilestone';
import Activity from './Activity';
import useTimeline from '../../hooks/useTimeline';

const ActivitiesTimeline = ({
  patientRecord,
  milestones,
  onMilestoneClick,
  isDischarged,
  rawFilters,
}) => {
  const { projectId } = patientRecord;
  const project = useSelector(ProjectSelect.one().whereIdEquals(projectId));
  const timeline = useTimeline({
    project,
    patientRecord,
    milestones,
    showOnlyOneTodayActivity: true,
  });

  // Extract selected milestone IDs from nested filter structure
  const selectedMilestoneIds = useMemo(() => {
    const flattenFilters = (filtersArray) => {
      if (!filtersArray || !Array.isArray(filtersArray)) {
        return [];
      }
      return flatMap(filtersArray, (f) => [
        f,
        ...(f.settings && f.settings.filters
          ? flattenFilters(f.settings.filters)
          : []),
      ]);
    };

    const allFilters = flattenFilters(rawFilters);
    const milestoneIdFilter = find(
      allFilters,
      (f) => f.settings && f.settings.valueKey === 'milestoneId',
    );
    return milestoneIdFilter?.state?.include || null;
  }, [rawFilters]);

  const activities = useMemo(() => {
    const milestoneNames = mapValues(keyBy(milestones, '_id'), 'name');

    let filteredActivities = patientRecord.activities;

    // If a milestone filter is active, only show activities with matching milestoneIds
    if (selectedMilestoneIds && selectedMilestoneIds.length > 0) {
      filteredActivities = filter(patientRecord.activities, (activity) =>
        selectedMilestoneIds.includes(activity.milestoneId),
      );
    }

    return compact(
      map(filteredActivities, (activity) => {
        if (milestoneNames[activity.milestoneId]) {
          return {
            state: activity.state,
            milestoneName: milestoneNames[activity.milestoneId],
          };
        }
        return null;
      }),
    );
  }, [milestones, patientRecord.activities, selectedMilestoneIds]);

  // Filter timeline activities based on selected milestones
  const filteredTimeline = useMemo(() => {
    // If no milestone filter, return timeline as-is
    if (!selectedMilestoneIds || selectedMilestoneIds.length === 0) {
      return timeline;
    }

    // Filter timeline activities
    const filterActivityByMilestone = (activity) =>
      activity && selectedMilestoneIds.includes(activity.milestoneId);

    return {
      left: filter(timeline.left, filterActivityByMilestone),
      today: filter(timeline.today, filterActivityByMilestone),
      right: filter(timeline.right, filterActivityByMilestone),
    };
  }, [timeline, selectedMilestoneIds]);

  return (
    <Timeline
      data-testid={`Timeline:${
        patientRecord.recipientName ||
        patientRecord.primaryIdentifier ||
        patientRecord.recipientId
      }`}
      data-test={JSON.stringify({
        activities,
      })}
      beforeNode={
        <MilestonesModal
          milestones={milestones}
          activities={initial(filteredTimeline.left)}
          patientRecord={patientRecord}
          onMilestoneClick={onMilestoneClick}
          isDischarged={isDischarged}
        />
      }
      items={[
        <Activity
          key="before"
          activity={last(filteredTimeline.left)}
          patientRecord={patientRecord}
          onMilestoneClick={onMilestoneClick}
          isDischarged={isDischarged}
        />,
        <Activity
          key="today"
          activity={filteredTimeline.today[0]}
          patientRecord={patientRecord}
          onMilestoneClick={onMilestoneClick}
          isDischarged={isDischarged}
        />,
        <Activity
          key="after"
          activity={first(filteredTimeline.right)}
          patientRecord={patientRecord}
          onMilestoneClick={onMilestoneClick}
          isDischarged={isDischarged}
        />,
      ]}
      afterNode={
        <MilestonesModal
          milestones={milestones}
          activities={tail(filteredTimeline.right)}
          patientRecord={patientRecord}
          onMilestoneClick={onMilestoneClick}
          isDischarged={isDischarged}
        />
      }
      disabled={isDischarged}
    />
  );
};

ActivitiesTimeline.propTypes = {
  patientRecord: PropTypes.instanceOf(PatientRecord).isRequired,
  milestones: PropTypes.arrayOf(PropTypes.instanceOf(ProjectMilestone)),
  onMilestoneClick: PropTypes.func,
  isDischarged: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  rawFilters: PropTypes.arrayOf(PropTypes.object),
};

ActivitiesTimeline.defaultProps = {
  milestones: [],
  onMilestoneClick: null,
  isDischarged: false,
  rawFilters: [],
};

export default ActivitiesTimeline;
